import clsx from "clsx";
import React, { type JSX } from "react";

import type { InputProps } from "../../../interfaces/input";
import { LoadingSkeleton } from "../../LoadingSkeleton";
import { InputError } from "../InputError";

const formatNumberToString = (num: number) => {
  if (!num) {
    return "";
  }

  return `${num}%`;
};

const formatStringToNumber = (str: string) => +str.replace("%", "");

const formatPercentage = (value: string) => {
  if (!value.length) {
    return "";
  }

  return value.replace(/(\d{2})/, "$1%").slice(0, 3);
};

export const InputPercentage = ({
  field,
  label,
  className,
  errorMessage,
  onChange,
  value,
  loadingValue,
  ...attributes
}: InputProps): JSX.Element => {
  const formatEventValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      target: { value: eventValue },
    } = event;
    const hasPressedBackSpace = value && !eventValue.includes("%");
    const formattedEventValue = (hasPressedBackSpace ? eventValue.slice(0, -1) : eventValue).replace(/\D/g, "");

    const newDisplayValue = formatPercentage(formattedEventValue);
    const newValue = formatStringToNumber(newDisplayValue);

    return {
      ...event,
      target: {
        ...event.target,
        value: newValue,
      },
    } as unknown as React.ChangeEvent<HTMLInputElement>;
  };

  return (
    <div className={clsx("flex flex-col", className)}>
      {label && (
        <label className={clsx("mb-1 lg:mb-0.5", attributes.disabled && "opacity-50")} htmlFor={field}>
          <b>{label}</b>
        </label>
      )}

      {loadingValue ? (
        <LoadingSkeleton kind="input" />
      ) : (
        <input
          {...attributes}
          type="text"
          onChange={(e) => {
            if (onChange) {
              onChange(formatEventValue(e));
            }
          }}
          className={clsx({ "outline-feedback-error": errorMessage })}
          id={field}
          name={field}
          value={formatNumberToString(value as number)}
        />
      )}

      <InputError message={errorMessage} />
    </div>
  );
};
